import React, { useCallback, useMemo, useRef, useState } from 'react';
import './SignalSparkline.css';

// The equity curve on a signal card, and the one place it is drawn.
//
// There were two of these — one on the dashboard, one in the marketplace — with the same maths
// copied into both and neither readable: a shape with no numbers on it, so the only way to find out
// what a signal was worth at a given moment was to open its detail page.
//
// No charting dependency. It is a polyline over normalised equity, with a pointer overlay that
// reports the sample under the cursor.

const VIEW_W = 100;
const VIEW_H = 30;
const PADDING = 1.5; // keeps the stroke from being clipped at the extremes

const money = (value) => `${value >= 0 ? '+' : ''}${Number(value).toFixed(2)}`;

/** Short and unambiguous at a glance: "27 Aug, 14:05". */
function formatMoment(time) {
  const at = new Date(Number(time));
  if (!Number.isFinite(at.getTime())) return '';
  return at.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * @param {Array<{t:number, v:number}>} points  Sample times and equity values.
 * @param {string} label  What the value means, for the tooltip and the screen-reader summary.
 */
function SignalSparkline({ points, label = 'Equity', className = '' }) {
  const [cursor, setCursor] = useState(null);
  const hostRef = useRef(null);

  const series = useMemo(() => (points || [])
    .map((point) => ({ t: Number(point.t), v: Number(point.v) }))
    .filter((point) => Number.isFinite(point.v)), [points]);

  const geometry = useMemo(() => {
    if (series.length < 2) return null;
    const values = series.map((point) => point.v);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const stepX = VIEW_W / (series.length - 1);
    const usable = VIEW_H - (PADDING * 2);
    const at = (index) => ({
      x: index * stepX,
      y: PADDING + (usable - (((series[index].v - min) / range) * usable)),
    });
    return {
      min,
      max,
      at,
      path: series.map((_, index) => {
        const { x, y } = at(index);
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      }).join(' '),
    };
  }, [series]);

  // Nearest sample to the pointer. Index rather than pixels, so it lands on real data and the
  // readout can never show a value the signal never had.
  const moveTo = useCallback((clientX) => {
    const host = hostRef.current;
    if (!host || series.length < 2) return;
    const box = host.getBoundingClientRect();
    if (!box.width) return;
    const fraction = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    setCursor(Math.round(fraction * (series.length - 1)));
  }, [series.length]);

  const onKeyDown = useCallback((event) => {
    if (series.length < 2) return;
    const last = series.length - 1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      setCursor((previous) => {
        const from = previous === null ? last : previous;
        return Math.min(last, Math.max(0, from + (event.key === 'ArrowRight' ? 1 : -1)));
      });
    } else if (event.key === 'Home') { event.preventDefault(); setCursor(0); } else if (event.key === 'End') { event.preventDefault(); setCursor(last); } else if (event.key === 'Escape') { setCursor(null); }
  }, [series.length]);

  if (!geometry) {
    return <div className={`signal-spark-empty ${className}`}>Track record starting…</div>;
  }

  const first = series[0];
  const latest = series[series.length - 1];
  const rising = latest.v >= first.v;
  const active = cursor === null ? null : series[cursor];
  const marker = cursor === null ? null : geometry.at(cursor);
  // Change since the start of the plotted window — the number the shape is actually showing.
  const delta = active ? active.v - first.v : latest.v - first.v;

  return (
    <div
      className={`signal-spark-host ${className}`}
      ref={hostRef}
      role="img"
      tabIndex={0}
      aria-label={`${label} from ${formatMoment(first.t)} to ${formatMoment(latest.t)}, `
        + `${money(latest.v - first.v)} over ${series.length} samples. `
        + 'Use the arrow keys to read individual points.'}
      onPointerMove={(event) => moveTo(event.clientX)}
      onPointerDown={(event) => moveTo(event.clientX)}
      onPointerLeave={() => setCursor(null)}
      onBlur={() => setCursor(null)}
      onKeyDown={onKeyDown}
    >
      <svg
        className="signal-spark"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <polyline points={geometry.path} className={rising ? 'up' : 'down'} />
      </svg>

      {marker && (
        <>
          {/* Percentage-positioned rather than drawn into the SVG: the chart is stretched to fit
              its box (preserveAspectRatio="none"), which would squash a circle into an ellipse. */}
          <span
            className="signal-spark-rule"
            style={{ left: `${(marker.x / VIEW_W) * 100}%` }}
          />
          <span
            className={`signal-spark-dot ${rising ? 'up' : 'down'}`}
            style={{ left: `${(marker.x / VIEW_W) * 100}%`, top: `${(marker.y / VIEW_H) * 100}%` }}
          />
        </>
      )}

      <div className="signal-spark-readout" aria-hidden="true">
        {active ? (
          <>
            <strong className={delta >= 0 ? 'up' : 'down'}>{money(delta)}</strong>
            <span>{formatMoment(active.t)}</span>
          </>
        ) : (
          <span className="signal-spark-hint">Hover to read</span>
        )}
      </div>
    </div>
  );
}

export default SignalSparkline;
